import { Center, CloseButton, Drawer, Group, Loader, Text, Button } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { useTranslation } from "react-i18next"
import { modals } from "@mantine/modals"
import { useCallback, useRef } from "react"
import { Trans } from "react-i18next"
import { useCrudItem } from "../../../hooks/useCrud"
import { DocumentCategorySchema } from "../models/documentCategory"
import type { DocumentCategory } from "../models/documentCategory"
import { useDeleteDocumentCategory } from "../hooks/useDocumentCategories"
import DocumentCategoryDetailContent from "./DocumentCategoryDetailContent"
import DocumentCategoryFormContent from "./DocumentCategoryFormContent"
import { useUser } from "../../user/hooks/useUser"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedId: number | null
  onDeleted: () => void
  onSwitchToEdit: () => void
  parentId?: number | null
}

export default function DocumentCategoryDrawer({ opened, onClose, mode, selectedId, onDeleted, onSwitchToEdit, parentId }: Props) {
  const { t } = useTranslation()
  const { data: item, isLoading } = useCrudItem<DocumentCategory>("document-categories", "document-category", DocumentCategorySchema, mode !== "create" ? selectedId : null)
  const { data: userData } = useUser()
  const canManage = userData?.can?.viewMenuDocumentCategories ?? false
  const deleteMutation = useDeleteDocumentCategory()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = useCallback(() => {
    if (!item?.id) return
    modals.openConfirmModal({
      title: t("documentCategories.confirmDelete"),
      children: <Text size="sm">{t("documentCategories.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(item.id, {
          onSuccess: () => onDeleted(),
          onError: () => {
            notifications.show({ color: "red", title: t("common.error"), message: t("common.error") })
          },
        })
      },
    })
  }, [item, deleteMutation, onDeleted, t])

  const title = mode === "create"
    ? t("documentCategories.createTitle")
    : mode === "edit"
    ? t("documentCategories.editTitle")
    : item?.name ?? ""

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size="md" withCloseButton={false}>
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md" lineClamp={1}>{title}</Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button size="xs" variant="default" onClick={onSwitchToEdit} leftSection={<i className="fa fa-pencil" />}>
                <Trans i18nKey="common.edit" />
              </Button>
              <Button size="xs" color="red" onClick={handleDelete} leftSection={<i className="fa fa-trash" />}>
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans i18nKey={mode === "edit" ? "common.update" : "common.create"} />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <DocumentCategoryFormContent key={parentId ?? "root"} ref={formRef} onSuccess={onClose} parentId={parentId} />
      ) : isLoading ? (
        <Center h="100%"><Loader /></Center>
      ) : !item ? (
        <Text p="md"><Trans i18nKey="common.notFound" /></Text>
      ) : mode === "edit" ? (
        <DocumentCategoryFormContent key={item.id} ref={formRef} item={item} onSuccess={onClose} />
      ) : (
        <DocumentCategoryDetailContent item={item} />
      )}
    </Drawer>
  )
}
